import React from 'react';
import { useTranslation } from 'react-i18next';
import { PublishState } from './types';
import styles from './PublishStatePill.module.css';

interface PublishStatePillProps {
  state: PublishState;
  scheduledAt?: string;
  publishedAt?: string;
  className?: string;
}

const PublishStatePill: React.FC<PublishStatePillProps> = ({ 
  state, 
  scheduledAt, 
  publishedAt,
  className 
}) => {
  const { t } = useTranslation('publishStatePill');
  
  const formatDateTime = (dateString?: string): string => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    const now = new Date();
    const diff = date.getTime() - now.getTime();
    const absDiff = Math.abs(diff);
    
    // Less than 1 minute
    if (absDiff < 60000) {
      return t('time.justNow');
    }
    
    // Less than 1 hour
    if (absDiff < 3600000) {
      const mins = Math.floor(absDiff / 60000);
      return diff > 0 ? t('time.minutesFromNow', { mins }) : t('time.minutesAgo', { mins });
    }
    
    // Less than 24 hours
    if (absDiff < 86400000) {
      const hours = Math.floor(absDiff / 3600000);
      return diff > 0 ? t('time.hoursFromNow', { hours }) : t('time.hoursAgo', { hours });
    }
    
    // Less than 7 days
    if (absDiff < 604800000) {
      const days = Math.floor(absDiff / 86400000);
      return diff > 0 ? t('time.daysFromNow', { days }) : t('time.daysAgo', { days });
    }
    
    // Format as date
    const options: Intl.DateTimeFormatOptions = {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    };
    
    // Add year if not current year
    if (date.getFullYear() !== now.getFullYear()) {
      options.year = 'numeric';
    }
    
    return date.toLocaleDateString('en-US', options);
  };

  const getConfig = () => {
    let timestamp = '';
    let prefix = '';

    switch (state) {
      case PublishState.DRAFT:
        return {
          label: t('states.draft'),
          className: styles.draft,
          icon: '📝',
          timestamp: '',
        };
      case PublishState.SCHEDULED:
        timestamp = formatDateTime(scheduledAt);
        prefix = timestamp ? ' · ' : '';
        return {
          label: t('states.scheduled'),
          className: styles.scheduled,
          icon: '📅',
          timestamp: prefix + timestamp,
        };
      case PublishState.PUBLISHING:
        return {
          label: t('states.publishing'),
          className: styles.publishing,
          icon: '🔄',
          timestamp: '',
        };
      case PublishState.LIVE_PUBLIC:
        timestamp = formatDateTime(publishedAt);
        prefix = timestamp ? ' · ' : '';
        return {
          label: t('states.livePublic'),
          className: styles.livePublic,
          icon: '🌐',
          timestamp: prefix + timestamp,
        };
      case PublishState.LIVE_UNLISTED:
        timestamp = formatDateTime(publishedAt);
        prefix = timestamp ? ' · ' : '';
        return {
          label: t('states.liveUnlisted'),
          className: styles.liveUnlisted,
          icon: '🔗',
          timestamp: prefix + timestamp,
        };
      case PublishState.LIVE_PRIVATE:
        timestamp = formatDateTime(publishedAt);
        prefix = timestamp ? ' · ' : '';
        return {
          label: t('states.livePrivate'),
          className: styles.livePrivate,
          icon: '🔒',
          timestamp: prefix + timestamp,
        };
      case PublishState.FAILED:
        return {
          label: t('states.failed'),
          className: styles.failed,
          icon: '❌',
          timestamp: '',
        };
      case PublishState.CANCELLED:
        return {
          label: t('states.cancelled'),
          className: styles.cancelled,
          icon: '🚫',
          timestamp: '',
        };
      default:
        return {
          label: t('states.unknown'),
          className: styles.draft,
          icon: '❓',
          timestamp: '',
        };
    }
  };

  const config = getConfig();

  return (
    <div className={`${styles.pill} ${config.className} ${className || ''}`}>
      <span className={styles.icon}>{config.icon}</span>
      <span className={styles.label}>
        {config.label}
        {config.timestamp && (
          <span className={styles.timestamp}>{config.timestamp}</span>
        )}
      </span>
    </div>
  );
};

export default PublishStatePill;
